import React, { forwardRef } from 'react';
import {
  asDocument,
  dateTime,
  Hairline,
  ReceiptAmount,
  ReceiptFooter,
  ReceiptHeader,
  Row,
  Section,
  Sheet,
} from 'components/receipt/ReceiptSheet';
import { formatCurrency } from 'utils/number';
import type { RefundReceiptData } from './refundReceiptData';

/**
 * Comprovante de estorno (cartão) e de devolução (Pix), igual ao do painel admin.
 * O que entra aqui já vem decidido por `refundReceiptData`; linha sem valor
 * simplesmente não aparece.
 *
 * `formatCurrency`, e não `maskCurrency`: o modo privacidade esconde valor na
 * tela, mas um comprovante sem o valor não comprova nada.
 */
const RefundReceipt = ({ data }: { data: RefundReceiptData }, ref: React.Ref<HTMLDivElement>) => {
  const isPix = data.method === 'PIX';
  const payerDocument = asDocument(data.payerDocument);
  const hasPayer = Boolean(data.payerName || payerDocument);

  return (
    <Sheet ref={ref}>
      <ReceiptHeader title={isPix ? 'Comprovante de Devolução Pix' : 'Comprovante de Estorno'} />

      <ReceiptAmount label="Valor devolvido" amount={formatCurrency(data.amount)} caption={dateTime(data.refundedAt)} />

      <div style={{ marginTop: '24px' }}>
        {hasPayer && (
          <>
            <Section title="Pagador">
              <Row label="Nome" value={data.payerName} />
              <Row label="CPF/CNPJ" value={payerDocument} />
            </Section>
            <Hairline />
          </>
        )}

        <Section title="Pagamento original">
          <Row label="Valor" value={formatCurrency(data.originalAmount)} />
          <Row label="Data" value={dateTime(data.paidAt)} />
          <Row label="Cartão" value={data.card} />
          <Row label="Bandeira" value={data.brand} />
          <Row label="Parcelas" value={data.installments ? `${data.installments}x` : null} />
          <Row label="Autorização" value={data.authorizationCode} />
          <Row label="NSU" value={data.nsu} />
          <Row label="End-to-end ID" value={data.endToEndId} />
          <Row label="TxID" value={data.txId} />
        </Section>
      </div>

      <Hairline />

      <Section title="Identificação">
        <Row label="Tipo" value={isPix ? 'DEVOLUÇÃO PIX' : 'ESTORNO DE CARTÃO'} />
        <Row label="ID da devolução" value={data.refundId} />
        <Row label="Cobrança" value={data.sequence ? `#${data.sequence}` : null} />
        <Row label="ID da cobrança" value={data.chargeId} />
        <Row label="Descrição" value={data.description} />
      </Section>

      <Hairline />

      <ReceiptFooter />
    </Sheet>
  );
};

export default forwardRef(RefundReceipt);
